<script setup lang="ts">
import { useLazyAsyncData, useNuxtApp } from "#app";
import { computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useRecipeCollectionStore } from "~/stores/recipe/recipeCollection";
import { useRecipesPageStore } from "~/stores/page/recipesPage";
import { clearAdsenseBlocks } from "~/utils/adsense";

definePageMeta({
    alias: '/recipes-page-:page',
});

const route = useRoute();
const recipesPageStore = useRecipesPageStore();
const recipeCollectionStore = useRecipeCollectionStore();
const page = computed(() => route?.params?.page ? Number(route?.params.page) : 1);
const filterIngredient = computed(() => _isString(route.query?.ingredients) ? route.query?.ingredients.split(',') : []);
const filterCuisine = computed(() => _isString(route.query?.cuisines) ? route.query?.cuisines.split(',') : []);

useLazyAsyncData('recipesPage', async () => recipesPageStore.fetch());
useLazyAsyncData('recipeCollection', async () => recipeCollectionStore.fetchList(page.value, undefined, filterIngredient.value, filterCuisine.value));

if (typeof recipeCollectionStore.error === "object") {
    throw recipeCollectionStore.error;
}

if (typeof recipesPageStore.error === "object") {
    throw recipesPageStore.error;
}

const load = async () => {
    await recipeCollectionStore.fetchList(page.value, undefined, filterIngredient.value, filterCuisine.value);
    if (typeof recipeCollectionStore.error === "object") {
        throw recipeCollectionStore.error;
    }
}

watch(() => route.query, () => {
    load();
});

const textBeforeContent = computed(() => recipesPageStore.textBeforeContent ? useNuxtApp().marked().parse(recipesPageStore.textBeforeContent) : recipesPageStore.textBeforeContent);
const textAfterContent = computed(() => recipesPageStore.textAfterContent ? useNuxtApp().marked().parse(recipesPageStore.textAfterContent) : recipesPageStore.textAfterContent);
const recipes = computed(() => recipeCollectionStore.list);
const skeleton = computed(() => recipeCollectionStore.skeleton);
const recipesError = computed(() => recipeCollectionStore.error);
const pagination = computed(() => recipeCollectionStore.pagination);

onMounted(() => {
    window.scrollTo(0, 0);
});

onUnmounted(() => {
    clearAdsenseBlocks();
    recipeCollectionStore.clear();
});
</script>

<template>
    <section class="text-gray-600 body-font container">
        <div class="md:flex md:flex-row">
            <aside class="mb-12 md:mb-initial md:w-2/5 lg:w-1/4 md:pr-8">
                <div class="lg:border-2 lg:border-indigo-500 lg:rounded-lg lg:p-4">
                    <FilterRecipe base-path="/recipes"/>
                </div>
                <div class="hidden md:block space-y-6 mt-10">
                    <LastInterestingFactsByCategoryToAside :category-id="2" :page-size="4"/>
                    <ClientOnly>
                        <AdsenseMedia/>
                    </ClientOnly>
                    <LastInterestingFactsByCategoryToAside :category-id="4" :page-size="4"/>
                    <ClientOnly>
                        <AdsenseMedia/>
                    </ClientOnly>
                    <LastInterestingFactsByCategoryToAside :category-id="3" :page-size="4"/>
                    <ClientOnly>
                        <AdsenseMedia/>
                    </ClientOnly>
                    <LastInterestingFactsByCategoryToAside :category-id="1" :page-size="4"/>
                    <ClientOnly>
                        <AdsenseMedia/>
                    </ClientOnly>
                </div>
            </aside>

            <div class="md:w-3/5 lg:w-3/4 space-y-12">
                <div v-if="textBeforeContent !== undefined" class="prose w-full max-w-full leading-relaxed text-gray-500" v-html="textBeforeContent"></div>
                <div v-else>
                    <span class="animate-pulse block rounded h-4 w-[30%] bg-gray-100"></span>
                    <span class="animate-pulse block rounded h-4 w-[50%] bg-gray-100 mt-4"></span>
                    <span class="animate-pulse block rounded h-4 w-[40%] bg-gray-100 mt-4"></span>
                </div>

<!--                <ClientOnly>
                    <section class="flex container justify-center">
                        <AdsenseSmallHorizontal/>
                    </section>
                </ClientOnly>-->

                <div v-if="recipes !== undefined && recipes.length > 0" class="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    <CardRecipe v-for="recipe in recipes"
                                :key="recipe.slug"
                                :title="recipe.title"
                                :picture-url="recipe.mainPicture"
                                :time="recipe.totalTime"
                                :detail-url="recipe.url"
                                :category="recipe.category"
                                class="h-full"
                    />
                </div>
                <div v-else-if="recipes === undefined && skeleton === true && !recipesError" class="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    <CardRecipe v-for="index in 10" :key="index" class="h-full" :skeleton="true"/>
                </div>
                <div v-else-if="recipes !== undefined && !recipesError && recipes.length <= 0">
                    <p class="text-lg leading-6 text-gray-900 font-bold">Nothing was found for the specified filter. Try selecting other search criteria.</p>
                </div>
                <AlertErrorMessage v-else-if="recipesError" :message="recipesError" />
                <PaginationDefault v-if="pagination?.pageCount > 1"
                                   :page-count="pagination?.pageCount"
                                   :page="pagination?.page"
                                   base-path="/recipes"
                />
                <ClientOnly>
                    <section class="hidden sm:flex container justify-center">
                        <AdsenseMediaBigWidth/>
                    </section>
                    <section class="sm:hidden container flex justify-center">
                        <AdsenseMedia/>
                    </section>
                </ClientOnly>
                <div v-if="textAfterContent !== undefined" class="prose w-full max-w-full leading-relaxed text-gray-500" v-html="textAfterContent"></div>
                <div v-else>
                    <span class="animate-pulse block rounded h-4 w-[30%] bg-gray-100"></span>
                    <span class="animate-pulse block rounded h-4 w-[50%] bg-gray-100 mt-4"></span>
                    <span class="animate-pulse block rounded h-4 w-[40%] bg-gray-100 mt-4"></span>
                </div>
            </div>
        </div>
    </section>
</template>